import React, { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import camelCase from "lodash/camelCase";
import SvgMore from "@mui/icons-material/ExpandMore";
import { Link as RouterLink, useNavigate } from "react-router";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import Tooltip from "@mui/material/Tooltip";

import caller from "../../utils/caller";
import { usePermissions } from "../../contexts/permissions";
import useNotification from "../../hooks/notification";
import useMutation from "../../hooks/useMutation";
import useQueryParams from "../../hooks/useQueryParams";
import ConfirmModal from "../ConfirmModal";

const styles = {
  circularProgress: { mr: 0.5 },
  row: {
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      bgcolor: "primary.25",
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    "&.Mui-selected": {
      bgcolor: "primary.50",
      "&:hover": {
        bgcolor: "primary.75",
      },
    },
    "& .MuiTableCell-root": {
      py: 2.5,
      px: 3,
      fontSize: "0.875rem",
      borderBottom: "1px solid",
      borderColor: "grey.100",
    },
  },
  rowNested: {
    "& > *": {
      borderBottom: "unset",
    },
  },
  nestedCell: { pt: 0, pb: 0 },
  nestedBox: { mb: 2 },
};

const RemoveButton = ({ loading, onClick, ...props }) => (
  <ConfirmModal onConfirm={onClick} title="Delete Record">
    <IconButton disabled={loading} {...props}>
      {loading ? (
        <CircularProgress size={20} sx={styles.circularProgress} />
      ) : (
        <DeleteOutlinedIcon />
      )}
    </IconButton>
  </ConfirmModal>
);

export default function EntityTableRow({
  configuration,
  setEntities,
  entity,
  selected,
  setSelected,
}) {
  const tableWithActions =
    configuration.editLink || configuration.customLink || configuration.removeMutation || configuration.actions;

  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const expandedKey = `expanded_${configuration.key}`;
  const expandedIds = (queryParams.get(expandedKey) || "").split(",").filter(Boolean);
  const extendedTable = expandedIds.includes(entity.id);

  const toggleNestedTable = () => {
    let newExpandedIds;

    if (extendedTable) {
      newExpandedIds = expandedIds.filter(id => id !== entity.id);
    } else {
      newExpandedIds = [...expandedIds, entity.id];
    }

    if (newExpandedIds.length > 0) {
      queryParams.set(expandedKey, newExpandedIds.join(","));
    } else {
      queryParams.delete(expandedKey);
    }

    navigate({ search: queryParams.toString() });
  };

  const [removeLoading, setRemoveLoading] = useState(false);
  const { can } = usePermissions();
  const { notifySuccess, notifyError } = useNotification();

  const canWrite = configuration.accessKey ? can(`${configuration.accessKey}.write`) : true;

  const canRemove = configuration.accessKey ? can(`${configuration.accessKey}.destroy`) : true;

  const [removeAction] =
    canRemove && configuration.removeMutation ? useMutation(configuration.removeMutation) : [null];

  const handleRemoveAction = () => {
    setRemoveLoading(true);

    return removeAction({
      variables: { id: entity.id },
      onSuccess: () => {
        setRemoveLoading(false);
        notifySuccess("Record has been successfully removed!");
        if (setEntities) {
          setEntities(prev => prev.filter(entityItem => entityItem.id !== entity.id));
        }
      },
      onFailure: errors => {
        setRemoveLoading(false);
        notifyError(errors);
      },
    });
  };

  return (
    <>
      <TableRow
        id={`tableRow-${configuration.key}-${entity.id}`}
        hover
        selected={entity.id === selected}
        onClick={() => setSelected(entity.id)}
        sx={{
          ...styles.row,
          ...(configuration.nestedTable && styles.rowNested),
        }}
      >
        {configuration.fields.map(field => {
          const value = caller(entity, field.path);

          const key = `tableCell-${configuration.key}-${entity.id}-${camelCase(field.label)}`;

          return (
            <TableCell id={key} key={key} align={field.headerAlign || "left"}>
              {field.component ? <field.component entity={entity} value={value} /> : value}
            </TableCell>
          );
        })}
        {configuration.nestedTable && (
          <TableCell>
            <Tooltip title={configuration.nestedTable.arrowTooltip}>
              <IconButton
                data-open={extendedTable}
                id={`nested-${configuration.key}-${entity.id}-${configuration.nestedTable.key}`}
                onClick={toggleNestedTable}
              >
                <SvgMore />
              </IconButton>
            </Tooltip>
          </TableCell>
        )}
        {tableWithActions && (
          <TableCell align="right">
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              {configuration.editLink && canWrite && (
                <IconButton
                  disabled={removeLoading}
                  id={`editLink-${configuration.key}-${entity.id}`}
                  component={RouterLink}
                  to={configuration.editLink.path(entity)}
                >
                  <EditOutlinedIcon />
                </IconButton>
              )}
              {configuration.customLink && (
                <configuration.customLink
                  disabled={removeLoading}
                  entity={entity}
                  setEntities={setEntities}
                />
              )}
              {configuration.actions &&
                configuration.actions.map((Action, index) => (
                  <Action
                    key={`action-${index}`}
                    entity={entity}
                    setEntities={setEntities}
                    disabled={removeLoading}
                  />
                ))}
              {removeAction && (
                <RemoveButton
                  id={`removeLink-${configuration.key}-${entity.id}`}
                  loading={removeLoading}
                  onClick={handleRemoveAction}
                />
              )}
            </Box>
          </TableCell>
        )}
      </TableRow>
      {configuration.nestedTable && (
        <TableRow>
          <TableCell sx={styles.nestedCell} colSpan={configuration.fields.length + 2}>
            <Collapse in={extendedTable} timeout="auto" unmountOnExit>
              <Box sx={styles.nestedBox}>
                <configuration.nestedTable.component entity={entity} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
